import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { id } from '@/services/legalControl/workflow'
import { evidence } from '@/services/legalControl/sourceReview'
import * as api from '@/services/legalControl/sourceProposal'

export default function SourceProposalForm({
  account,
  session,
  blocked,
}: {
  account: string
  session: string
  blocked: boolean
}) {
  const { hasPermission } = useAuth()
  const [initial] = useState(() => {
    try {
      return { intent: api.pending(account), error: '' }
    } catch {
      return {
        intent: null,
        error:
          'Saved proposal intent unavailable. Recover storage before creating another proposal.',
      }
    }
  })
  const [intent, setIntent] = useState(initial.intent),
    [anchor, setAnchor] = useState(''),
    [firm, setFirm] = useState(''),
    [source, setSource] = useState('')
  const [draft, setDraft] = useState(''),
    [view, setView] = useState<api.Context | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(initial.error),
    [message, setMessage] = useState('')
  const [reference, setReference] = useState(''),
    [sha, setSha] = useState(''),
    [expiry, setExpiry] = useState(() => new Date(Date.now() + 3600000).toISOString().slice(0, 16)),
    [ack, setAck] = useState(false)
  const [settled, setSettled] = useState(false)
  const epoch = useRef(0)
  useEffect(
    () => () => {
      epoch.current++
    },
    []
  )
  const allowed = hasPermission('txh.platform.legal_control.subject.inspect')
  function change(write: (v: string) => void, v: string) {
    epoch.current++
    write(v)
    setView(null)
    setAck(false)
    setError('')
    setMessage('')
    setDraft('')
  }
  async function preview() {
    if (busy || intent || blocked || initial.error) return
    const version = ++epoch.current
    setBusy(true)
    setView(null)
    setAck(false)
    setError('')
    setMessage('')
    try {
      const query = {
        review_id: draft || crypto.randomUUID(),
        anchor_request_id: id(anchor),
        case_id: id(source),
        firm_id: id(firm),
      }
      setDraft(query.review_id)
      const result = await api.preview(query)
      if (version === epoch.current) setView(result)
    } catch {
      if (version === epoch.current)
        setError(
          'Source preview unavailable. Check the references,access and MFA; no proposal was submitted.'
        )
    } finally {
      if (version === epoch.current) setBusy(false)
    }
  }
  async function submit() {
    if (busy || intent || blocked || initial.error || !view || !ack) return
    const version = ++epoch.current
    setBusy(true)
    setError('')
    setMessage('')
    let saved = false
    try {
      const expires = new Date(expiry + 'Z')
      if (
        !Number.isFinite(expires.getTime()) ||
        expires.getTime() <= Date.now() ||
        expires.getTime() > Date.now() + 86400000
      )
        throw new Error('Finite future expiry required')
      const cmd: api.Command = {
        review_id: view.review_id,
        anchor_request_id: view.anchor_request_id,
        case_id: view.case_id,
        firm_id: view.firm_id,
        expected_context_sha256: view.context_sha256,
        evidence_reference: evidence(reference),
        evidence_sha256: sha,
        expires_at: expires.toISOString(),
        acknowledge_review_only: true,
      }
      if (!/^[0-9a-f]{64}$/.test(sha)) throw new Error('Evidence fingerprint required')
      const original = {
        review: cmd.review_id,
        context: cmd.expected_context_sha256,
        fingerprint: await api.fingerprint(cmd),
        user: account,
        session,
      }
      if (version !== epoch.current) return
      api.remember(original)
      saved = true
      setIntent(original)
      setAck(false)
      await api.send(cmd)
      if (version === epoch.current) {
        setView(null)
        setMessage(
          'Proposal sent. Recover its original outcome to confirm the recorded provenance.'
        )
      }
    } catch (failure) {
      if (version === epoch.current) {
        if (saved) setView(null)
        if (saved && api.refusedBeforeWrite(failure)) {
          try {
            api.forget(account)
            setIntent(null)
            setError(
              'This attempt was refused before writing. Keep the original draft reference and preview again.'
            )
          } catch {
            setError('Original intent storage unavailable; keep its reference for recovery.')
          }
        } else
          setError(
            saved
              ? 'Proposal outcome unknown. Recover the original proposal; do not create a replacement.'
              : 'Proposal not sent. Check evidence,expiry and original-intent storage.'
          )
      }
    } finally {
      if (version === epoch.current) setBusy(false)
    }
  }
  async function recover() {
    if (busy || !intent) return
    const version = ++epoch.current
    setBusy(true)
    setView(null)
    setError('')
    setMessage('')
    try {
      const result = await api.recover(intent)
      if (version !== epoch.current) return
      if (result === 'unresolved') {
        setMessage('Original outcome remains unresolved. Keep the original reference.')
        return
      }
      api.forget(account)
      setIntent(null)
      setDraft(intent.review)
      setSettled(true)
      setMessage(
        result === 'matched'
          ? 'Original proposal confirmed with matching provenance. Use its review reference below.'
          : 'Another immutable outcome is recorded. Your submission is not confirmed; inspect the original review.'
      )
    } catch {
      if (version === epoch.current)
        setError(
          'Original proposal remains unresolved. Check access/MFA and recover this same reference; no replacement was sent.'
        )
    } finally {
      if (version === epoch.current) setBusy(false)
    }
  }
  if (!allowed) return <p>Creating proposals requires both inspection and review permission.</p>
  const locked = busy || !!intent || blocked || !!initial.error || settled
  return (
    <section className="card p-5 space-y-3" aria-label="Create source relationship proposal">
      <h2 className="font-semibold">Create source relationship proposal</h2>
      <p>
        Select known verification and source references. The server supplies source facts and
        history. Evidence is declared here; this proposal grants no ownership or access.
      </p>
      <label>
        Original verification request
        <input
          className="input w-full"
          disabled={locked}
          value={anchor}
          onChange={e => change(setAnchor, e.target.value)}
        />
      </label>
      <label>
        Source firm reference
        <input
          className="input w-full"
          disabled={locked}
          value={firm}
          onChange={e => change(setFirm, e.target.value)}
        />
      </label>
      <label>
        Source case reference
        <input
          className="input w-full"
          disabled={locked}
          value={source}
          onChange={e => change(setSource, e.target.value)}
        />
      </label>
      <button
        className="btn"
        disabled={locked || !anchor || !firm || !source}
        onClick={() => void preview()}
      >
        Preview source relationship
      </button>
      {draft && (
        <p>
          Original draft reference: <span>{draft}</span>
        </p>
      )}
      {intent && (
        <>
          <p role="status">Saved proposal reference: {intent.review}</p>
          <button className="btn" disabled={busy} onClick={() => void recover()}>
            Recover original proposal
          </button>
        </>
      )}
      {settled && (
        <button
          className="btn"
          onClick={() => {
            epoch.current++
            setSettled(false)
            setDraft('')
            setAnchor('')
            setFirm('')
            setSource('')
            setReference('')
            setSha('')
            setView(null)
            setAck(false)
            setError('')
            setMessage('')
            setExpiry(new Date(Date.now() + 3600000).toISOString().slice(0, 16))
          }}
        >
          Start another proposal
        </button>
      )}
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
      {view && (
        <div className="space-y-3" aria-label="Live proposal preview">
          <p>Client: {view.client_id}</p>
          <p>HUF subject: {view.subject_id}</p>
          <p>Source customer: {String(view.target.customer_id)}</p>
          <p>
            Period: {String(view.target.period_basis)} {String(view.target.period_value)}
          </p>
          <p>Source revision: {String(view.target.current_revision)}</p>
          <label>
            Proposal evidence reference
            <input
              className="input w-full"
              disabled={locked}
              value={reference}
              maxLength={256}
              onChange={e => {
                setReference(e.target.value)
                setAck(false)
              }}
            />
          </label>
          <label>
            Proposal evidence fingerprint
            <input
              className="input w-full"
              disabled={locked}
              value={sha}
              maxLength={64}
              onChange={e => {
                setSha(e.target.value)
                setAck(false)
              }}
            />
          </label>
          <label>
            Review expiry (UTC)
            <input
              className="input w-full"
              type="datetime-local"
              disabled={locked}
              value={expiry}
              onChange={e => {
                setExpiry(e.target.value)
                setAck(false)
              }}
            />
          </label>
          <label>
            <input
              type="checkbox"
              disabled={locked}
              checked={ack}
              onChange={e => setAck(e.target.checked)}
            />{' '}
            I reviewed this source,history and declared evidence. This submission grants no
            authority.
          </label>
          <button className="btn" disabled={locked || !ack} onClick={() => void submit()}>
            Submit observed proposal
          </button>
        </div>
      )}
    </section>
  )
}
